// @owners { team: patients }
import { useNavigation } from '@alto/navigation';
import React, { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
// eslint-disable-next-line @alto/prefer-shared-redux-hooks
import { useStore } from '~shared/redux';
import { useSelectorShared } from '~shared/store';
import { urlFromOnboardState } from '~web/features/onboarding/helpers/urlHandler';
import getUserOnboardState from '~web/features/onboarding/selectors/getUserOnboardState';
import { useGetInitialData, useIsOnboardingRoute } from '~web/hooks';

type AuthRequiredProps = {
  readonly isUsingNextParam?: boolean;
};

export const AuthRequired = ({ isUsingNextParam = true }: AuthRequiredProps) => {
  const hasFetched = useRef(false); // ref to fetch initial data
  const store = useStore();
  const { getInitialData } = useGetInitialData({ store }); // get initial data after patient authenticates

  const authorized = useSelectorShared((state) => state.auth.authorized);
  const onboardState = useSelectorShared(getUserOnboardState);
  const onboardingUrl = urlFromOnboardState(onboardState);
  const { isOnboardingRoute } = useIsOnboardingRoute();
  const { navigate } = useNavigation();
  const location = useLocation();

  useEffect(() => {
    if (!authorized) {
      if (isUsingNextParam) {
        const nextPath = `/login?next=${location.pathname}`;
        navigate(nextPath, { replace: true });
      } else {
        navigate('RouteLogin');
      }
    }

    if (!isOnboardingRoute && onboardingUrl) {
      navigate(onboardingUrl, { replace: true });
    }
  }, [authorized, onboardingUrl, isOnboardingRoute, navigate, location.pathname, isUsingNextParam]);

  useEffect(() => {
    if (authorized && !hasFetched.current) {
      getInitialData();
      hasFetched.current = true;
    }
  }, [authorized, getInitialData]);

  // Outlet renders any matching child and nested routes
  return authorized ? <Outlet /> : null;
};
